import { getEnv } from "@/lib/get-env"
import { getAccessTokenJwt } from "@/lib/auth/auth-store"

type RequestOptions = Omit<RequestInit, "body"> & {
	params?: Record<string, string | number | undefined>
}

export class ApiRequestError extends Error {
	readonly code: string
	readonly status: number
	readonly details?: unknown

	constructor(args: { readonly code: string; readonly status: number; readonly message?: string; readonly details?: unknown }) {
		const permissionDenied = args.status === 403 && ["permission_denied", "lab_document_forbidden", "forbidden"].includes(args.code)
		super(permissionDenied ? "You do not have permission to access this resource or perform this action." : args.message ?? "API request failed")
		this.name = "ApiRequestError"
		this.code = args.code
		this.status = args.status
		if (args.details !== undefined) {
			this.details = args.details
		}
	}
}

function getPortalApiBaseUrl(): URL {
	const raw = getEnv("VITE_API_BASE_URL")
	let base: URL
	try {
		base = new URL(raw)
	} catch {
		throw new Error(`VITE_API_BASE_URL must be a valid URL, got: ${raw}`)
	}

	const normalizedPath = base.pathname.endsWith("/") ? base.pathname.slice(0, -1) : base.pathname
	const apiPrefix = "/api/v1/portal"
	if (!normalizedPath.endsWith(apiPrefix)) {
		const nextPath = normalizedPath.length === 0 ? apiPrefix : `${normalizedPath}${apiPrefix}`
		base = new URL(nextPath, base.origin)
	}

	return base
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
	const base = getPortalApiBaseUrl()
	const cleanPath = path.startsWith("/") ? path.slice(1) : path
	const fullPathname = base.pathname.endsWith("/")
		? base.pathname + cleanPath
		: base.pathname + "/" + cleanPath

	const url = new URL(fullPathname, base.origin)
	if (params) {
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) url.searchParams.set(key, String(value))
		}
	}
	return url.toString()
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		let code = "api_error"
		let message: string | undefined = undefined
		let details: unknown = undefined
		try {
			const payload = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } }
			if (payload.error !== undefined) {
				if (typeof payload.error.code === "string" && payload.error.code.length > 0) {
					code = payload.error.code
				}
				if (typeof payload.error.message === "string" && payload.error.message.length > 0) {
					message = payload.error.message
				}
				if (payload.error.details !== undefined) {
					details = payload.error.details
				}
			}
		} catch {
			// Ignore invalid or empty error bodies and use the generic message/code.
		}

		throw new ApiRequestError({
			code,
			status: response.status,
			...(message === undefined ? {} : { message }),
			...(details === undefined ? {} : { details }),
		})
	}
	return response.json() as Promise<T>
}

type CachedToken = {
	readonly jwt: string
	readonly expiresAtMs: number
}

let cachedToken: CachedToken | null = null

export function clearApiAuthCache(): void {
	cachedToken = null
}

function decodeBase64UrlToUtf8(input: string): string {
	const b64 = input.replace(/-/g, "+").replace(/_/g, "/")
	const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")
	return atob(padded)
}

function getJwtExpMs(jwt: string): number | null {
	const parts = jwt.split(".")
	if (parts.length !== 3) return null
	const payloadB64 = parts[1]
	if (typeof payloadB64 !== "string" || payloadB64.length === 0) return null
	let payloadRaw: string
	try {
		payloadRaw = decodeBase64UrlToUtf8(payloadB64)
	} catch {
		return null
	}
	let payload: unknown
	try {
		payload = JSON.parse(payloadRaw)
	} catch {
		return null
	}
	if (!payload || typeof payload !== "object") return null
	const exp = (payload as Record<string, unknown>)["exp"]
	if (typeof exp !== "number" || !Number.isFinite(exp)) return null
	return exp * 1000
}

function isTokenFresh(token: CachedToken, skewMs: number): boolean {
	return Date.now() + skewMs < token.expiresAtMs
}

async function getAuthHeader(args: { forceRefresh: boolean }): Promise<Record<string, string>> {
	const cached = cachedToken
	if (cached && isTokenFresh(cached, 30_000) && !args.forceRefresh) {
		return { Authorization: `Bearer ${cached.jwt}` }
	}

	const jwt = await getAccessTokenJwt({ forceRefresh: args.forceRefresh })
	if (!jwt) {
		cachedToken = null
		return {}
	}

	const expMs = getJwtExpMs(jwt)
	if (expMs === null) {
		// If we can't decode exp, don't cache; still allow the request.
		cachedToken = null
		return { Authorization: `Bearer ${jwt}` }
	}

	cachedToken = { jwt, expiresAtMs: expMs }
	return { Authorization: `Bearer ${jwt}` }
}

async function fetchWithAuth(url: string, init: RequestInit): Promise<Response> {
	const authHeader = await getAuthHeader({ forceRefresh: false })
	const firstHeaders = new Headers(init.headers)
	for (const [key, value] of Object.entries(authHeader)) {
		firstHeaders.set(key, value)
	}
	const first = await fetch(url, { ...init, headers: firstHeaders })

	if (first.status !== 401) return first

	const refreshedAuthHeader = await getAuthHeader({ forceRefresh: true })
	const secondHeaders = new Headers(init.headers)
	for (const [key, value] of Object.entries(refreshedAuthHeader)) {
		secondHeaders.set(key, value)
	}
	return await fetch(url, { ...init, headers: secondHeaders })
}

async function request(path: string, method: string, options: RequestOptions = {}, body?: unknown, json = true): Promise<Response> {
	const { params, headers: extraHeaders, ...restInit } = options
	const headers = new Headers(extraHeaders)
	if (json) headers.set("Content-Type", "application/json")
	return fetchWithAuth(buildUrl(path, params), {
		method,
		headers,
		...(method === "POST" || method === "PATCH" || method === "PUT" ? { body: body === undefined ? null : JSON.stringify(body) } : {}),
		...restInit,
	})
}

export const api = {
	get: async <T>(path: string, options?: RequestOptions): Promise<T> => handleResponse<T>(await request(path, "GET", options)),
	getBlob: async (path: string, options?: RequestOptions): Promise<Blob> => {
		const response = await request(path, "GET", options, undefined, false)
		if (!response.ok) await handleResponse<never>(response)
		return response.blob()
	},
	post: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> => handleResponse<T>(await request(path, "POST", options, body)),
	postForm: async <T>(path: string, body: FormData): Promise<T> => handleResponse<T>(await fetchWithAuth(buildUrl(path), { method: "POST", body })),
	patch: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> => handleResponse<T>(await request(path, "PATCH", options, body)),
	put: async <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> => handleResponse<T>(await request(path, "PUT", options, body)),
	delete: async <T>(path: string, options?: RequestOptions): Promise<T> => handleResponse<T>(await request(path, "DELETE", options)),
}
